/* eslint-disable react-hooks/static-components */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Trash2,
  Edit,
  CalendarDays,
  List,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Badge } from '@/app/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/simple-toaster';
import {
  useReservations,
  useCreateReservation,
  useUpdateReservation,
  useDeleteReservation,
} from '@/hooks/use-reservations';
import { useOrgToday } from '@/hooks/use-settings';
import { AssetPicker } from '@/app/components/inventory/asset-picker';
import { formatDateOnly } from '@/lib/date-utils';
import type { AssetReservation } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

export function ReservationsPage() {
  const { canManageGear } = usePermissions();
  const orgToday = useOrgToday();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<AssetReservation | null>(null);

  const [formData, setFormData] = useState({
    asset: '',
    reserved_from: '',
    reserved_until: '',
    purpose: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const assetFilter = searchParams.get('asset') || '';

  const { data: reservationsData, isLoading, error } = useReservations({
    page,
    page_size: PAGE_SIZE,
    asset: assetFilter ? parseInt(assetFilter, 10) : '',
    ordering: '-reserved_from',
  });

  const createReservation = useCreateReservation();
  const updateReservation = useUpdateReservation();
  const deleteReservation = useDeleteReservation();

  const reservations = reservationsData?.results || [];
  const totalCount = reservationsData?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const activeReservations = reservations.filter((res) => {
    const fromDate = res.reserved_from.slice(0, 10);
    const toDate = res.reserved_until.slice(0, 10);
    return fromDate <= orgToday && toDate >= orgToday;
  });
  const upcomingReservations = reservations.filter(
    (res) => res.reserved_from.slice(0, 10) > orgToday
  );

  const updateFilters = (updates: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    if (!updates.page) {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage: number) => {
    updateFilters({ page: newPage.toString() });
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const resetForm = () => {
    setFormData({
      asset: '',
      reserved_from: '',
      reserved_until: '',
      purpose: '',
    });
    setFormError(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (reservation: AssetReservation) => {
    setSelectedReservation(reservation);
    setFormData({
      asset: reservation.asset.toString(),
      reserved_from: reservation.reserved_from,
      reserved_until: reservation.reserved_until,
      purpose: reservation.purpose || '',
    });
    setFormError(null);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (reservation: AssetReservation) => {
    setSelectedReservation(reservation);
    setIsDeleteDialogOpen(true);
  };

  const validateForm = (): boolean => {
    if (!formData.asset) {
      setFormError('Please select an asset');
      return false;
    }
    if (!formData.reserved_from) {
      setFormError('Please select a start date');
      return false;
    }
    if (!formData.reserved_until) {
      setFormError('Please select an end date');
      return false;
    }
    if (new Date(formData.reserved_until) < new Date(formData.reserved_from)) {
      setFormError('End date must be after start date');
      return false;
    }
    setFormError(null);
    return true;
  };

  const handleCreateReservation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await createReservation.mutateAsync({
        asset: parseInt(formData.asset, 10),
        reserved_from: formData.reserved_from,
        reserved_until: formData.reserved_until,
        purpose: formData.purpose || undefined,
      });

      toast.success('Reservation Created', {
        description: 'The asset has been successfully reserved.',
      });

      resetForm();
      setIsCreateDialogOpen(false);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string; non_field_errors?: string[] } } };
      const detail = axiosError?.response?.data?.detail;
      const nonFieldErrors = axiosError?.response?.data?.non_field_errors?.[0];
      
      toast.error('Error', {
        description: detail || nonFieldErrors || 'Failed to create reservation. There may be a conflict with an existing reservation.',
      });
    }
  };

  const handleUpdateReservation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReservation || !validateForm()) return;

    try {
      await updateReservation.mutateAsync({
        id: selectedReservation.id,
        data: {
          asset: parseInt(formData.asset, 10),
          reserved_from: formData.reserved_from,
          reserved_until: formData.reserved_until,
          purpose: formData.purpose || undefined,
        },
      });

      toast.success('Reservation Updated', {
        description: 'The reservation has been successfully updated.',
      });

      resetForm();
      setIsEditDialogOpen(false);
      setSelectedReservation(null);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string; non_field_errors?: string[] } } };
      const detail = axiosError?.response?.data?.detail;
      const nonFieldErrors = axiosError?.response?.data?.non_field_errors?.[0];
      
      toast.error('Error', {
        description: detail || nonFieldErrors || 'Failed to update reservation. There may be a conflict with an existing reservation.',
      });
    }
  };

  const handleDeleteReservation = async () => {
    if (!selectedReservation) return;

    try {
      await deleteReservation.mutateAsync(selectedReservation.id);

      toast.success('Reservation Deleted', {
        description: 'The reservation has been successfully deleted.',
      });

      setIsDeleteDialogOpen(false);
      setSelectedReservation(null);
    } catch {
      toast.error('Error', {
        description: 'Failed to delete reservation. Please try again.',
      });
    }
  };

  const getReservationStatus = (reservation: AssetReservation) => {
    const fromDate = reservation.reserved_from.slice(0, 10);
    const toDate = reservation.reserved_until.slice(0, 10);
    if (toDate < orgToday) return 'completed';
    if (fromDate <= orgToday && toDate >= orgToday) return 'active';
    return 'upcoming';
  };

  const ReservationCard = ({ reservation }: { reservation: AssetReservation }) => {
    const status = getReservationStatus(reservation);
    return (
      <Card className={status === 'completed' ? 'opacity-60' : ''}>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{reservation.asset_name}</h3>
              <p className="text-sm text-gray-600 mt-1">{reservation.purpose || 'No purpose specified'}</p>

              <div className="mt-4 space-y-2">
                <div className="flex items-center text-sm">
                  <span className="text-gray-600 w-24">Reserved by:</span>
                  <span className="font-medium">{reservation.user_name}</span>
                </div>
                <div className="flex items-center text-sm">
                  <span className="text-gray-600 w-24">Duration:</span>
                  <span className="font-medium">
                    {formatDateOnly(reservation.reserved_from, '—', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    {' → '}
                    {formatDateOnly(reservation.reserved_until, '—', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div className="ml-4 flex flex-col items-end gap-2">
              {status === 'active' && <Badge className="bg-green-600">Active</Badge>}
              {status === 'upcoming' && <Badge variant="secondary">Upcoming</Badge>}
              {status === 'completed' && <Badge variant="outline">Completed</Badge>}

              {status !== 'completed' && (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(reservation)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDeleteDialog(reservation)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const CalendarView = () => {
    const currentMonth = parseISO(orgToday);
    const daysInMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0
    ).getDate();
    const firstDayOfMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1
    ).getDay();

    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dateValue = format(date, 'yyyy-MM-dd');
      const dayReservations = reservations.filter((res) => {
        const from = res.reserved_from.slice(0, 10);
        const until = res.reserved_until.slice(0, 10);
        return dateValue >= from && dateValue <= until;
      });

      days.push(
        <div key={day} className="h-24 border p-1 overflow-hidden">
          <div className="text-sm font-medium text-gray-700">{day}</div>
          <div className="space-y-1 mt-1">
            {dayReservations.slice(0, 2).map((res) => (
              <div
                key={res.id}
                className="text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded truncate"
                title={res.asset_name}
              >
                {res.asset_name}
              </div>
            ))}
            {dayReservations.length > 2 && (
              <div className="text-xs text-gray-500">
                +{dayReservations.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            {formatDateOnly(orgToday, '—', { month: 'long', year: 'numeric' })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="bg-gray-100 p-2 text-center text-sm font-medium">
                {day}
              </div>
            ))}
            {days}
          </div>
        </CardContent>
      </Card>
    );
  };

  const ReservationForm = ({
    onSubmit,
    isLoading: formLoading,
    submitLabel,
  }: {
    onSubmit: (e: React.FormEvent) => void;
    isLoading: boolean;
    submitLabel: string;
  }) => (
    <form onSubmit={onSubmit}>
      <div className="space-y-4 py-4">
        {formError && (
          <div className="text-sm text-red-500 bg-red-50 p-2 rounded">{formError}</div>
        )}

        <div className="space-y-2">
          <Label htmlFor="form-asset">Asset *</Label>
          <AssetPicker
            value={formData.asset || null}
            onChange={(value) => setFormData({ ...formData, asset: value })}
            placeholder="Select an asset"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="from-date">From Date *</Label>
            <Input
              id="from-date"
              type="date"
              value={formData.reserved_from}
              onChange={(e) => setFormData({ ...formData, reserved_from: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="to-date">To Date *</Label>
            <Input
              id="to-date"
              type="date"
              value={formData.reserved_until}
              onChange={(e) => setFormData({ ...formData, reserved_until: e.target.value })}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="purpose">Purpose</Label>
          <Textarea
            id="purpose"
            placeholder="Describe the purpose of this reservation"
            value={formData.purpose}
            onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
            rows={3}
          />
        </div>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
          }}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={formLoading}>
          {formLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Reservations</h1>
          <p className="text-gray-600 mt-1">Schedule and manage future asset usage</p>
        </div>

        <div className="flex gap-2">
          <div className="flex border rounded-lg">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
            >
              <CalendarDays className="h-4 w-4" />
            </Button>
          </div>
          {canManageGear && (
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              New Reservation
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Reservations</CardTitle>
            <Calendar className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : activeReservations.length}
            </div>
            <p className="text-xs text-gray-600 mt-1">Currently in use</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Upcoming Reservations</CardTitle>
            <Calendar className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : upcomingReservations.length}
            </div>
            <p className="text-xs text-gray-600 mt-1">Scheduled for future</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <AssetPicker
                value={assetFilter || null}
                onChange={(value) => updateFilters({ asset: value })}
                placeholder="Filter by asset"
                allowNone
                noneLabel="All Assets"
                noneValue=""
              />
            </div>
            {assetFilter && (
              <Button variant="ghost" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">
          Error loading reservations. Please try again.
        </div>
      ) : viewMode === 'calendar' ? (
        <CalendarView />
      ) : (
        <>
          <div>
            <h2 className="text-xl font-semibold mb-4">All Reservations</h2>
            {reservations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  No reservations found
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reservations.map((reservation) => (
                  <ReservationCard key={reservation.id} reservation={reservation} />
                ))}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-gray-600">
                Showing {(page - 1) * PAGE_SIZE + 1} to{' '}
                {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} results
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Reservation</DialogTitle>
            <DialogDescription>Reserve an asset for future use</DialogDescription>
          </DialogHeader>
          <ReservationForm
            onSubmit={handleCreateReservation}
            isLoading={createReservation.isPending}
            submitLabel="Create Reservation"
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Reservation</DialogTitle>
            <DialogDescription>Update the reservation details</DialogDescription>
          </DialogHeader>
          <ReservationForm
            onSubmit={handleUpdateReservation}
            isLoading={updateReservation.isPending}
            submitLabel="Update Reservation"
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reservation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this reservation for{' '}
              <strong>{selectedReservation?.asset_name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteReservation}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteReservation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
